import { useState, useMemo } from 'react';
import { Check, ChevronsUpDown, MapPin } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { INDIAN_STATES, getDistrictsByState, searchDistricts } from '@/data/indianDistricts';

/**
 * District Select Component
 * Two-step selection: First select state, then select district
 * Or search directly for any district
 */
export default function DistrictSelect({ 
  value, 
  onChange, 
  placeholder = "Select district",
  disabled = false 
}) {
  const [open, setOpen] = useState(false);
  const [selectedState, setSelectedState] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [mode, setMode] = useState('state'); // 'state' | 'district' | 'search'

  // Parse current value
  const [currentDistrict, currentState] = useMemo(() => {
    if (!value) return ['', ''];
    const parts = value.split(', ');
    return parts.length === 2 ? parts : [value, ''];
  }, [value]);

  // Search results
  const searchResults = useMemo(() => {
    if (searchQuery.length < 2) return [];
    return searchDistricts(searchQuery);
  }, [searchQuery]);

  // Districts for selected state
  const districtsForState = useMemo(() => {
    return selectedState ? getDistrictsByState(selectedState) : [];
  }, [selectedState]);

  const handleStateSelect = (state) => {
    setSelectedState(state);
    setMode('district');
    setSearchQuery('');
  };

  const handleDistrictSelect = (district, state) => {
    onChange(`${district}, ${state}`);
    setOpen(false);
    setMode('state');
    setSelectedState('');
    setSearchQuery('');
  };

  const handleSearchChange = (query) => {
    setSearchQuery(query);
    if (query.length >= 2) {
      setMode('search');
    } else if (selectedState) {
      setMode('district');
    } else {
      setMode('state');
    }
  };

  const handleBack = () => {
    setMode('state');
    setSelectedState('');
    setSearchQuery('');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
          disabled={disabled}
          data-testid="district-select-trigger"
        >
          {value ? (
            <span className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              {value}
            </span>
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[350px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput 
            placeholder="Search state or district..." 
            value={searchQuery}
            onValueChange={handleSearchChange}
          />
          <CommandList className="max-h-[300px]">
            {/* Search Mode - Show search results */}
            {mode === 'search' && searchResults.length > 0 && (
              <CommandGroup heading="Search Results">
                {searchResults.map((item) => (
                  <CommandItem
                    key={`${item.district}-${item.state}`}
                    value={item.fullName}
                    onSelect={() => handleDistrictSelect(item.district, item.state)}
                    data-testid={`district-option-${item.district}`}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value === item.fullName ? "opacity-100" : "opacity-0"
                      )}
                    />
                    <div>
                      <span className="font-medium">{item.district}</span>
                      <span className="text-muted-foreground text-xs ml-2">{item.state}</span>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {/* Search Mode - No results */}
            {mode === 'search' && searchResults.length === 0 && searchQuery.length >= 2 && (
              <CommandEmpty>No district found.</CommandEmpty>
            )}

            {/* State Selection Mode */}
            {mode === 'state' && searchQuery.length < 2 && (
              <CommandGroup heading="Select State">
                {INDIAN_STATES.map((state) => (
                  <CommandItem
                    key={state}
                    value={state}
                    onSelect={() => handleStateSelect(state)}
                    data-testid={`state-option-${state}`}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        currentState === state ? "opacity-100" : "opacity-0"
                      )}
                    />
                    {state}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {/* District Selection Mode */}
            {mode === 'district' && searchQuery.length < 2 && (
              <>
                <div className="flex items-center gap-2 px-2 py-1.5 border-b">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={handleBack}
                    className="h-6 text-xs"
                  >
                    ← Back
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    Districts in <strong>{selectedState}</strong>
                  </span>
                </div>
                <CommandGroup>
                  {districtsForState.map((district) => (
                    <CommandItem
                      key={district}
                      value={district}
                      onSelect={() => handleDistrictSelect(district, selectedState)}
                      data-testid={`district-option-${district}`}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          currentDistrict === district && currentState === selectedState ? "opacity-100" : "opacity-0"
                        )}
                      />
                      {district}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
